import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, ChoiceChips, CopyButton, Field, Icon, Input, Segmented, Textarea, Tooltip, cn, initialsOf, type IconName } from "@sagot/ui";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { useAudienceLabels } from "../shared/queries";
import type { AgentConfig, AgentStatus } from "../shared/types";
import { GreetingCard } from "./greeting-card";
import { AVATAR_PRESETS, LIMITS, type SettingsValues } from "./settings-model";
import { SettingsCard } from "./settings-ui";

const PRESET_ICONS: Readonly<Record<string, IconName | null>> = { bot: "bot", initials: null, sparkles: "sparkles", building: "building", headset: "headset" };
const PRESET_TONES: Readonly<Record<string, string>> = {
  bot: "bg-primary text-on-primary",
  initials: "bg-ink-950 text-white",
  sparkles: "bg-accent text-ink-950",
  building: "bg-surface-2 text-primary-ink",
  headset: "bg-ink-700 text-white",
};
const STATUSES: readonly AgentStatus[] = ["live", "paused", "draft"];
const AUDIENCES = ["1", "2", "3", "4"] as const;

export function AvatarMark({ preset, name, size = "md" }: { preset: string; name: string; size?: "md" | "lg" }) {
  const icon = PRESET_ICONS[preset] ?? "bot";
  return (
    <span aria-hidden="true" className={cn("inline-flex items-center justify-center rounded-lg font-extrabold", size === "lg" ? "size-14 text-lg" : "size-10 text-xs", PRESET_TONES[preset] ?? PRESET_TONES.bot)}>
      {preset === "initials" ? initialsOf(name) : <Icon name={icon} className={size === "lg" ? "size-7" : "size-5"} />}
    </span>
  );
}

function AvatarPicker({ disabled }: { disabled: boolean }) {
  const { t } = useTranslation();
  const form = useFormContext<SettingsValues>();
  const name = useWatch({ control: form.control, name: "name" });
  return (
    <Controller
      control={form.control}
      name="avatar"
      render={({ field }) => (
        <fieldset>
          <legend className="label">{t("build.agentSettings.general.avatar")}</legend>
          <div className="flex flex-wrap items-center gap-4">
            <AvatarMark preset={field.value} name={name} size="lg" />
            <div className="flex flex-wrap gap-2" role="group" aria-label={t("build.agentSettings.general.avatar")}>
              {AVATAR_PRESETS.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  aria-pressed={field.value === preset}
                  aria-label={t(`build.agentSettings.general.avatarPresets.${preset}`)}
                  disabled={disabled}
                  onClick={() => { field.onChange(preset); }}
                  className="rounded-md border-2 border-line p-0.5 aria-pressed:border-fg disabled:opacity-60"
                >
                  <AvatarMark preset={preset} name={name} />
                </button>
              ))}
            </div>
          </div>
          <p className="hint">{t("build.agentSettings.general.avatarHint")}</p>
        </fieldset>
      )}
    />
  );
}

interface GeneralTabProps {
  config: AgentConfig;
  disabled: boolean;
  canPublish: boolean;
}

export function GeneralTab({ config, disabled, canPublish }: GeneralTabProps) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const form = useFormContext<SettingsValues>();
  const label = useAudienceLabels();
  const errors = form.formState.errors;
  return (
    <>
      <SettingsCard title={t("build.agentSettings.general.identity")} hint={t("build.agentSettings.general.identityHint")}>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <Field label={t("build.agentSettings.general.name")} hint={t("build.agentSettings.general.nameHint")} error={errors.name?.message}>
            <Input maxLength={LIMITS.nameMax} disabled={disabled} {...form.register("name")} />
          </Field>
          <Field label={t("build.agentSettings.general.agentId")} hint={t("build.agentSettings.general.agentIdHint")}>
            <div className="flex gap-2">
              <Input readOnly value={config.public_key} className="font-mono text-xs" />
              <CopyButton value={config.public_key} label={t("build.agentSettings.general.copyId")} copiedLabel={t("common.copied")} />
            </div>
          </Field>
        </div>
        <AvatarPicker disabled={disabled} />
        <Field label={t("build.agentSettings.general.description")} hint={t("build.agentSettings.general.descriptionHint")}>
          <Textarea rows={2} maxLength={LIMITS.descriptionMax} disabled={disabled} {...form.register("description")} />
        </Field>
      </SettingsCard>
      <GreetingCard config={config} disabled={disabled} />
      <SettingsCard title={t("build.agentSettings.general.availability")}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-sm font-semibold" id="agent-status-label">{t("build.agentSettings.general.status")}</div>
            <p className="text-xs text-fg-subtle">{t("build.agentSettings.general.statusHint")}</p>
          </div>
          <Controller
            control={form.control}
            name="status"
            render={({ field }) => {
              const control = (
                <Segmented
                  aria-label={t("build.agentSettings.general.status")}
                  value={field.value}
                  onValueChange={(next) => { if (!disabled && (next !== "live" || canPublish)) field.onChange(next); }}
                  options={STATUSES.map((s) => ({ value: s, label: t(`build.status.${s}`) }))}
                />
              );
              return canPublish || disabled ? control : <Tooltip content={t("build.agentSettings.publishNeeded")}><span>{control}</span></Tooltip>;
            }}
          />
        </div>
        <div className="divider" />
        <div>
          <div className="text-sm font-semibold" id="agent-audience-label">{t("build.agentSettings.general.audience")}</div>
          <p className="text-xs text-fg-subtle">
            {t("build.agentSettings.general.audienceHint")}{" "}
            <AppLink href={consoleRoutes.audiences(summary.slug)} className="font-semibold text-primary-ink hover:underline">{t("build.agentSettings.general.audienceLink")}</AppLink>
          </p>
          <Controller
            control={form.control}
            name="audiences"
            render={({ field }) => (
              <ChoiceChips
                multiple
                aria-label={t("build.agentSettings.general.audience")}
                className="mt-3"
                options={AUDIENCES.map((code) => ({ value: code, label: label(Number(code)), disabled }))}
                value={field.value.map(String)}
                onValueChange={(next) => { if (next.length > 0) field.onChange(next.map(Number)); }}
              />
            )}
          />
          <p className="hint">{errors.audiences?.message ?? t("build.agentSettings.general.audiencePublicHint")}</p>
        </div>
      </SettingsCard>
    </>
  );
}
